import { z } from 'zod';
import { TFunction } from 'i18next';
import { StudyTimeOptions } from '@/constants/studyTimeOptions';

const contactSchema = z.object({
  name: z.string().default(''),
  email: z.string().default(''),
  phone: z.string().default(''),
});

/** One avrech row of step 2/3: picked in step 2, optionally given its own schedule in step 3. */
const avrechRowSchema = z.object({
  supportedId: z.number(),
  firstName: z.string().default(''),
  lastName: z.string().default(''),
  /** Set only when scheduleMode is 'different'; undefined falls back to defaultStudyTime. */
  studyTimeOverride: z.enum(StudyTimeOptions).optional(),
  /** Set when this avrech was already in a different kollel and the coordinator confirmed the
   * "בקשה להעברת כולל" dialog for them (HAT-1446 phase 7) - carried through to ConfirmTransfer on
   * submit. Undefined means a plain add, no transfer involved. */
  transferFromKollelId: z.number().optional(),
  transferFromKollelName: z.string().optional(),
});

/**
 * Schema of the "כולל חדש" wizard (HAT-1446 phases 3-4). Mirrors the Figma spec's steps: details +
 * avrechs, the "סדרי לימוד" branch, the stipend sub-caps, and the legal declaration.
 *
 * The stipend fields are the kollel's OWN policy (set by its coordinator) - no office number is
 * hard-coded here. The total ceiling they are checked against (KollelSettings.MaxTotalStipendPerAvrech)
 * is fetched from the server in KollelStipendStep and is normally null (not yet approved), in
 * which case the step accepts any total.
 */
export const createKollelFormSchema = (t: TFunction) => z.object({
  // Step 1: פרטי כולל
  name: z.string().default(''),
  address: z.string().default(''),
  roshKollelContact: contactSchema.default({ name: '', email: '', phone: '' }),
  supervisingBodyContact: contactSchema.default({ name: '', email: '', phone: '' }),
  // Step 2: שיוך אברכים
  avrechs: z.array(avrechRowSchema).default([]),
  // Step 3: סדרי לימוד
  scheduleMode: z.enum(['same', 'different']).default('same'),
  defaultStudyTime: z.enum(StudyTimeOptions).default(StudyTimeOptions.Morning),
  // Step 4: מלגות אברכים (HAT-1446 phase 4)
  excellenceLimit: z.number().nullable().default(null),
  examsLimit: z.number().nullable().default(null),
  punctualityLimit: z.number().nullable().default(null),
  // Step 5: הצהרה משפטית
  legalTermsApproved: z.boolean().default(false),
})
  .refine((data) => data.name.trim().length > 0, {
    message: t('kollels.wizard.validation.nameRequired'), path: ['name'],
  })
  .refine((data) => data.legalTermsApproved, {
    message: t('kollels.wizard.validation.legalRequired'), path: ['legalTermsApproved'],
  });

export type KollelFormData = z.input<ReturnType<typeof createKollelFormSchema>>;

export const defaultKollelFormValues: KollelFormData = {
  name: '',
  address: '',
  roshKollelContact: { name: '', email: '', phone: '' },
  supervisingBodyContact: { name: '', email: '', phone: '' },
  avrechs: [],
  scheduleMode: 'same',
  defaultStudyTime: StudyTimeOptions.Morning,
  excellenceLimit: null,
  examsLimit: null,
  punctualityLimit: null,
  legalTermsApproved: false,
};
